import { useState, useEffect, useContext } from 'react'
import xlsxFile from 'read-excel-file'
import { postPerformancesData } from '../../Assets/Interfaces/CalculationInterface'
import { GetAllDataByProduct } from '../../Assets/Interfaces/ProductInterface'
import { GetAllStatusCandidatesByName } from '../../Assets/Interfaces/CandidatesInterface'
import { GlobalContext } from './../../store/index'

export default function useExcelParse() {
    const [productData, setProductData] = useState([])
    const [statusData, setStatusData] = useState([])
    const [dataToInsert, setDataToInsert] = useState([])
    const [excelFile, setExcelFile] = useState([])
    const [excelPage, setExcelPage] = useState([])
    const [excelData, setExcelData] = useState([])
    const [model, setModel] = useState()
    const [status, setStatus] = useState('stopped')
    const [productDataLoading, setProductDataLoading] = useState(false)
    const [state] = useContext(GlobalContext)

    useEffect(() => {
        if (model) {
            setProductDataLoading(true)
            setProductData([])
            GetAllDataByProduct({ name: model })
                .then((res) => {
                    setProductData(res && res.length ? res : [])
                })
                .finally(() => setProductDataLoading(false))

            GetAllStatusCandidatesByName(model).then((res) => {
                if (res && res.length) setStatusData(res)
            })
        }
    }, [model])
    const [importResult, setImportResult] = useState([])
    const [loading, setLoading] = useState(false)

    const handleChange = (event) => {
        setExcelFile([])
        setExcelPage([])
        setExcelData([])
        setImportResult([])
        setExcelFile([...event.target.files])
    }

    const filterExcelData = (types) => {
        return new Promise((resolve, reject) => {
            const filteredData = excelData.filter(({ type }) => types.includes(type))
            setExcelData(filteredData)

            resolve(filteredData)
        })
    }

    const sizesMatch = (candidateValue, excelValue) => {
        const normalizedCandidate = typeof candidateValue === 'string' ? candidateValue.trim() : candidateValue
        const normalizedExcel = typeof excelValue === 'string' ? excelValue.trim() : excelValue

        if (normalizedCandidate === normalizedExcel) return true

        const candidateAsNumber = Number(normalizedCandidate)
        const excelAsNumber = Number(normalizedExcel)
        if (!Number.isNaN(candidateAsNumber) && !Number.isNaN(excelAsNumber)) {
            return candidateAsNumber === excelAsNumber
        }

        return String(normalizedCandidate).toUpperCase() === String(normalizedExcel).toUpperCase()
    }

    const normalizeOptionValue = (value) => {
        if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'
        const stringValue = String(value ?? '').trim().toUpperCase()
        if (['TRUE', 'YES', '1'].includes(stringValue)) return 'TRUE'
        if (['FALSE', 'NO', '0', ''].includes(stringValue)) return 'FALSE'
        return stringValue
    }

    const parseOptionCodeFromName = (name) => {
        const match = /^Opt_b(.+?)#/.exec(name ?? '')
        return match ? match[1].trim() : null
    }

    const getCandidateBySize = (size) => {
        const candidates = productData.filter((product) =>
            product.Parameters.some((Parameter) => Parameter.Name === 'Sel_sSize' && sizesMatch(Parameter.Values[0].Value, size))
        )
        return candidates
    }

    const findOptionParameter = (candidate, key) => {
        const trimmedKey = key?.toString().trim()
        return candidate.Parameters.find(
            (Parameter) =>
                Parameter.Type === 'in' &&
                (Parameter.Description?.toString().trim() === trimmedKey ||
                    parseOptionCodeFromName(Parameter.Name) === trimmedKey)
        )
    }

    const getCandidateByOptions = (options, candidates) => {
        const candidateList = candidates.filter((candidate) =>
            Object.entries(options).every(([key, value]) => {
                const matchingParameter = findOptionParameter(candidate, key)
                if (!matchingParameter) {
                    return normalizeOptionValue(value) === 'FALSE'
                }
                return normalizeOptionValue(matchingParameter.Values[0].Value) === normalizeOptionValue(value)
            })
        )
        return candidateList[0]?.candidateId
    }

    const explainMatchFailure = (size, optionsList, candidatesBySize) => {
        if (!candidatesBySize.length) {
            const availableSizes = [
                ...new Set(
                    productData
                        .map((product) => product.Parameters.find((p) => p.Name === 'Sel_sSize')?.Values?.[0]?.Value)
                        .filter((value) => value !== undefined)
                ),
            ]
            return { reason: 'size-not-found', availableSizes }
        }

        const candidateDiffs = candidatesBySize.map((candidate) => {
            const mismatches = Object.entries(optionsList)
                .map(([key, expectedValue]) => {
                    const matchingParameter = findOptionParameter(candidate, key)
                    const actualValue = matchingParameter ? matchingParameter.Values?.[0]?.Value : undefined
                    const normalizedActual = matchingParameter ? normalizeOptionValue(actualValue) : 'FALSE'
                    const normalizedExpected = normalizeOptionValue(expectedValue)
                    if (normalizedActual === normalizedExpected) return null
                    return { option: key, expected: expectedValue, actual: matchingParameter ? actualValue : '(not set / FALSE)' }
                })
                .filter(Boolean)
            return { candidateId: candidate.candidateId, mismatches }
        })

        return { reason: 'options-mismatch', candidateDiffs }
    }

    const getCandidateId = (perfList, optionsList, type, size, model) => {
        const candidateBySize = getCandidateBySize(size)
        return getCandidateByOptions(optionsList, candidateBySize)
    }
    const insertData = async (data) => {
        const output = await postPerformancesData(data, state.user.name)
        if (output.result == 'success') {
            setStatus('success')
            setTimeout(() => {
                document.location.reload()
            }, 2000)
        } else {
            setStatus('error')
        }
    }
    const handleSubmit = (types, model) => {
        if (productDataLoading) {
            console.error('handleSubmit: candidate data for this model is still loading, please wait and try again.')
            return
        }
        filterExcelData(types).then((filteredData) => {
            let dataToInsert = []

            filteredData.map(({ perfList, optionsList, type, size }, index) => {
                const candidateId = getCandidateId(perfList, optionsList, type, size, model)
                if (candidateId && perfList.length > 0 && model) {
                    const insertObj = {
                        perfList,
                        candidateId,
                        model,
                        type,
                    }
                    dataToInsert.push(insertObj)
                } else {
                    const candidatesBySize = getCandidateBySize(size)
                    const diagnostic = explainMatchFailure(size, optionsList, candidatesBySize)
                    if (diagnostic.reason === 'size-not-found') {
                        console.error(
                            `Row Error On the Line ${index}: no candidate exists for size "${size}".`,
                            'Available sizes for this model:',
                            diagnostic.availableSizes
                        )
                    } else {
                        console.error(
                            `Row Error On the Line ${index}: found ${diagnostic.candidateDiffs.length} candidate(s) with size "${size}", but none match the requested options.`,
                            'Requested options:',
                            optionsList,
                            'Mismatches per candidate:',
                            diagnostic.candidateDiffs
                        )
                    }
                }
            })
            if (dataToInsert.length === filteredData.length) {
                insertData(dataToInsert)
                setDataToInsert(dataToInsert)
            } else {
                setStatus('error')
            }
        })
    }

    const getExcelPage = (file) => {
        return xlsxFile(file, { getSheets: true }).then((rows) =>
            rows.filter(({ name }) => !name.startsWith('XX')).map((el) => ({ name: el.name, fileName: file.name }))
        )
    }

    const getAllPages = async () => {
        setLoading(true)
        const pages = await Promise.all(excelFile.map((file) => getExcelPage(file)))
        pages.map((page) => {
            page.map(({ name, fileName }) => {
                setExcelPage((prev) => [...prev, { name, fileName }])
            })
        })
        setLoading(false)
    }

    const optionsConstructor = (optArray, optNames) => {
        let optionsObj = {}
        optArray.map((option, index) => {
            const optionName = optNames[index]?.toString().trim()
            optionsObj[optionName] = option
        })
        return optionsObj
    }

    const perfConstructor = (perfValues, perfNames, perfUnities) => {
        const excludedPerfNames = ['calcstatus', 'date']
        let perfList = []

        perfValues.map((perf, index) => {
            const perfName = perfNames[index]?.toString().trim()
            if (!perfName) return null
            if (excludedPerfNames.includes(perfName.toLowerCase())) return null

            const perfObj = { name: perfName, unity: perfUnities[index], value: perf }
            perfList.push(perfObj)
        })

        return perfList
    }

    useEffect(() => {
        if (excelFile?.length > 0) {
            getAllPages()
        }
    }, [excelFile])

    useEffect(() => {
        if (excelPage.length > 0 && !loading) {
            excelPage.forEach((element) => {
                xlsxFile(
                    excelFile.find((el) => el.name === element.fileName),
                    { sheet: element.name }
                ).then((rows) => {
                    const performancePosition = rows[0].findIndex((row) => row === 'PERFORMANCES')
                    const optionsDesc = rows[1].slice(1, performancePosition)
                    const PerfNames = rows[1].slice(performancePosition, rows[1].length)
                    const PerfUnities = rows[2].slice(performancePosition, rows[1].length)
                    const values = rows.slice(3, rows.length)
                    values.forEach((col) => {
                        const size = col[0]
                        const optionsList = optionsConstructor(col.slice(1, performancePosition), optionsDesc)
                        const perfValues = col.slice(performancePosition, col.length)
                        const perfList = perfConstructor(perfValues, PerfNames, PerfUnities)
                        setExcelData((prev) => [
                            ...prev,
                            { perfList: perfList, optionsList: optionsList, type: element.name, size: size },
                        ])
                    })
                })
            })
        }
    }, [excelPage, loading])

    return { handleChange, handleSubmit, importResult, loading, excelPage, setModel, status, productDataLoading }
}
